import React from 'react';
import { Detector } from 'react-detect-offline';
import { Theme, Button } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import Spinner from 'components/Spinner';
import classNames from 'classnames';
import { SaveStatus } from 'services/types';
import { useTranslation } from 'services/localization/useTranslation';
import { newFirestoreId } from 'common/utils/dataMigrations';

interface OwnProps {
	status: SaveStatus;
	retrySave: () => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const SaveFeedback = (props: Props) => {
	const { status, classes } = props;
	const { t } = useTranslation();

	const getSaveFeedbackToRender = (status: string) => {
		let feedback: string;
		let error: boolean = false;
		let FeedBackIcon: () => JSX.Element;
		switch (status) {
			case 'SAVING':
				feedback = t('common:states.saving', 'Saving');
				FeedBackIcon = () => (
					<Spinner relative size={28} padding={6} thickness={4} color="#3BD691" />
				);
				break;
			case 'SAVED':
				feedback = t('common:states.saved', 'Saved');
				FeedBackIcon = () => <CheckIcon className={classNames(classes.icon, classes.check)} />;
				break;
			case 'NOT_SAVED':
				feedback = t('common:states.notSaved', 'Not saved');
				error = true;
				FeedBackIcon = () => <CloseIcon className={classes.icon} color="error" />;
				break;
			default:
				feedback = t('common:states.saved', 'Saved');
				FeedBackIcon = () => <CheckIcon className={classNames(classes.icon, classes.check)} />;
		}
		return { FeedBackIcon, feedback, error };
	};

	const { FeedBackIcon, feedback, error } = getSaveFeedbackToRender(status);

	const pollingConfig = {
		url: `/online-check.png?id=${newFirestoreId()}`,
		interval: 10000,
		timeout: 10000,
	};

	return (
		<Detector
			polling={pollingConfig}
			render={({ online }: { online: boolean }) => (
				<div className={classes.textCenter}>
					<Box className={classes.box}>
						{online ? (
							<>
								<FeedBackIcon />
								<Typography variant="caption">{feedback}</Typography>
							</>
						) : (
							<>
								<WifiOffIcon className={classes.icon} color="error" />
								<Typography variant="caption">{t('common:statusBar.offline')}</Typography>
							</>
						)}
					</Box>
					{error && (
						<Button className={classes.smallBtn} variant="text">
							<Typography variant="caption" color="primary" onClick={props.retrySave}>
								{t('common:actions.retry', 'Retry')}
							</Typography>
						</Button>
					)}
				</div>
			)}
		/>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		textCenter: {
			textAlign: 'center',
		},
		box: {
			minHeight: 30,
			paddingLeft: theme.spacing(0.5),
			paddingRight: theme.spacing(1.5),
			borderRadius: 24.5,
			boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.08)',
			border: `1px solid ${theme.palette.border.paper}`,
			backgroundColor: theme.palette.background.paper,
			position: 'relative',
			alignItems: 'center',
			display: 'flex',
		},
		smallBtn: {
			padding: 0,
			minWidth: 0,
			minHeight: 0,
			margin: 0,
			paddingTop: 4,
		},
		check: {
			color: theme.palette.success.main,
		},
		icon: {
			padding: theme.spacing(0.5),
		},
	});

export default withStyles(styles)(SaveFeedback);
